import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import {
    Stepper, Step, StepLabel, Button, Typography, Container, Box, TextField,
    AppBar, Toolbar, Tabs, Tab, Paper, Grid, InputAdornment, MenuItem, Select,
    FormControl, InputLabel, Avatar, IconButton, LinearProgress
} from '@mui/material';
import {
    PhotoCamera, WarningAmber, Description, Stethoscope, Person, FamilyRestroom,
    MedicalServices, Favorite as FavoriteIcon, Language as LanguageIcon,
    Logout as LogoutIcon, Settings as SettingsIcon, CheckCircleOutline as CheckCircleOutlineIcon,
    Event, Wc, Phone, Email, Lock, VisibilityOff, Visibility, Home, PhoneCallback, AccessTime
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import BackButton from "./BackButton";

const ChildRegistration = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const steps = [
        t('child_info_step'), 
        t('parent_info_step'), 
        t('medical_history_step')
    ];

    const [activeStep, setActiveStep] = useState(0);

    const [formData, setFormData] = useState({
        child_full_name: '',
        date_of_birth: '',
        gender: '',
        child_photo: null,
        parent_name: '',
        phone_number: '',
        email: '',
        password: '',
        home_address: '',
        relationship_to_child: '',
        emergency_contact_number: '',
        date_of_marriage: '',
        delivery_type: '',
        pregnancy_complications: '',
        delivery_complications: '',
        joining_date: new Date().toISOString().split('T')[0]
    });
    const [showPassword, setShowPassword] = useState(false);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        setFormData({ ...formData, child_photo: e.target.files[0] });
    };

    const handleLogout = () => {
        localStorage.removeItem('userRole');
        navigate('/login');
    };

    const changeLanguage = (event) => {
        i18n.changeLanguage(event.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const postData = new FormData();
        for (const key in formData) {
            postData.append(key, formData[key]);
        }
        
        const userRole = localStorage.getItem('userRole') || 'doctor';
        if (userRole !== 'admin') {
            postData.append('recommended_therapies', JSON.stringify([userRole]));
        } else {
            postData.append('recommended_therapies', JSON.stringify([]));
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/register_child.php`, postData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            const childId = response.data.child_id;
            navigate(`/assessment/${childId}`); // Redirect to assessment instead of dashboard
        } catch (error) {
            console.error("Failed to register child:", error);
            // Optionally, show an error message to the user
        }
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0: // Child Information
                return (
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Person sx={{ mr: 1, color: '#00bcd4' }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('child_information_title')}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('child_information_subtitle')}</Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <Avatar
                                src={formData.child_photo ? URL.createObjectURL(formData.child_photo) : ''}
                                sx={{ width: 80, height: 80, mb: 1, border: '2px dashed #bdbdbd' }}
                            >
                                {!formData.child_photo && <PhotoCamera sx={{ fontSize: 40, color: '#bdbdbd' }} />}
                            </Avatar>
                            <Button component="label" sx={{ textTransform: 'none' }}>
                                {t('add_photo_button')}
                                <input type="file" hidden onChange={handleFileChange} accept="image/*" />
                            </Button>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('child_full_name_label')}
                                    name="child_full_name"
                                    value={formData.child_full_name}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    placeholder={t('child_full_name_placeholder')}
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Person /></InputAdornment>) }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={t('date_of_birth_label')}
                                    name="date_of_birth"
                                    type="date"
                                    value={formData.date_of_birth}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Event /></InputAdornment>) }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>{t('gender_label')}</InputLabel>
                                    <Select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleChange}
                                        label={t('gender_label')}
                                        startAdornment={<InputAdornment position="start"><Wc /></InputAdornment>}
                                    >
                                        <MenuItem value="">{t('select_gender_placeholder')}</MenuItem>
                                        <MenuItem value="Male">{t('gender_male')}</MenuItem>
                                        <MenuItem value="Female">{t('gender_female')}</MenuItem>
                                        <MenuItem value="Other">{t('gender_other')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Joining Date"
                                    name="joining_date"
                                    type="date"
                                    value={formData.joining_date}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Event /></InputAdornment>) }}
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                variant="contained"
                                sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#00acc1' } }}
                                onClick={handleNext}
                            >
                                {t('continue_to_parent_info_button')}
                            </Button>
                        </Box>
                    </Paper>
                );
            case 1: // Parent Information
                return (
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <FamilyRestroom sx={{ mr: 1, color: '#00bcd4' }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('parent_information_title')}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('parent_information_subtitle')}</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('parent_guardian_name_label')}
                                    name="parent_name"
                                    value={formData.parent_name}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Person /></InputAdornment>) }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={t('phone_number_label')}
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Phone /></InputAdornment>) }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={t('email_address_label')}
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Email /></InputAdornment>) }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('create_password_label')}
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{
                                        startAdornment: (<InputAdornment position="start"><Lock /></InputAdornment>),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText={t('password_helper_text')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('home_address_label')}
                                    name="home_address"
                                    value={formData.home_address}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    multiline
                                    rows={3}
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Home /></InputAdornment>) }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>{t('relationship_to_child_label')}</InputLabel>
                                    <Select
                                        name="relationship_to_child"
                                        value={formData.relationship_to_child}
                                        onChange={handleChange}
                                        label={t('relationship_to_child_label')}
                                        startAdornment={<InputAdornment position="start"><FamilyRestroom /></InputAdornment>}
                                    >
                                        <MenuItem value="">{t('select_relationship_placeholder')}</MenuItem>
                                        <MenuItem value="Mother">{t('relationship_mother')}</MenuItem>
                                        <MenuItem value="Father">{t('relationship_father')}</MenuItem>
                                        <MenuItem value="Guardian">{t('relationship_guardian')}</MenuItem>
                                        <MenuItem value="Other">{t('relationship_other')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={t('emergency_contact_number_label')}
                                    name="emergency_contact_number"
                                    value={formData.emergency_contact_number}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><PhoneCallback /></InputAdornment>) }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('date_of_marriage_label')}
                                    name="date_of_marriage"
                                    type="date"
                                    value={formData.date_of_marriage}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Event /></InputAdornment>) }}
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <Button variant="outlined" onClick={handleBack}>
                                {t('previous_button')}
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#00acc1' } }}
                                onClick={handleNext}
                            >
                                {t('continue_to_medical_history_button')}
                            </Button>
                        </Box>
                    </Paper>
                );
            case 2: // Medical History
                return (
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <MedicalServices sx={{ mr: 1, color: '#00bcd4' }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('medical_history_title')}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('medical_history_subtitle')}</Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                            {t('birth_delivery_info_heading')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('birth_delivery_info_description')}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>{t('delivery_type_label')}</InputLabel>
                                    <Select
                                        name="delivery_type"
                                        value={formData.delivery_type}
                                        onChange={handleChange}
                                        label={t('delivery_type_label')}
                                        startAdornment={<InputAdornment position="start"><AccessTime /></InputAdornment>}
                                    >
                                        <MenuItem value="">{t('select_delivery_type_placeholder')}</MenuItem>
                                        <MenuItem value="Normal">{t('delivery_type_normal')}</MenuItem>
                                        <MenuItem value="C-Section">{t('delivery_type_c_section')}</MenuItem>
                                        <MenuItem value="Vacuum Extraction">{t('delivery_type_vacuum_extraction')}</MenuItem>
                                        <MenuItem value="Forceps Delivery">{t('delivery_type_forceps_delivery')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('pregnancy_complications_label')}
                                    name="pregnancy_complications"
                                    value={formData.pregnancy_complications}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    multiline
                                    rows={4}
                                    placeholder={t('pregnancy_complications_placeholder')}
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><WarningAmber /></InputAdornment>) }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('delivery_complications_label')}
                                    name="delivery_complications"
                                    value={formData.delivery_complications}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                    multiline
                                    rows={4}
                                    placeholder={t('delivery_complications_placeholder')}
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Description /></InputAdornment>) }}
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <Button variant="outlined" onClick={handleBack}>
                                {t('previous_button')}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#00acc1' } }}
                            >
                                {t('complete_registration_button')}
                            </Button>
                        </Box>
                    </Paper>
                );
            default:
                return 'Unknown step';
        }
    };

    const getStepIcon = (step) => {
        if (step < activeStep) {
            return <CheckCircleOutlineIcon color="success" />;
        }
        return null;
    };

    const totalSteps = steps.length;
    const completedSteps = activeStep;
    const progress = (completedSteps / totalSteps) * 100;

    const userRole = localStorage.getItem('userRole') || 'doctor';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Main Header */}
            <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: 'white' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ bgcolor: '#00bcd4', borderRadius: '8px', p: 0.5, display: 'flex' }}>
                            <FavoriteIcon sx={{ color: 'white', fontSize: '20px' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('akidscare_brand')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LanguageIcon sx={{ color: 'text.secondary' }} />
                            <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                <Select value={i18n.language} onChange={changeLanguage} disableUnderline>
                                    <MenuItem value="en">EN</MenuItem>
                                    <MenuItem value="te">TE</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>{userRole === 'admin' ? "DR. PAVAN" : `Therapist (${userRole.charAt(0).toUpperCase() + userRole.slice(1)})`} ({userRole})</Typography>
                        <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>{t('logout_button')}</Button>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Navigation Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Tabs value={1} aria-label="registration tabs" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                        <Tab 
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SettingsIcon /><Typography>{userRole === 'admin' ? "Admin Dashboard" : t('doctor_dashboard_tab')}</Typography></Box>} 
                            component={Link} to="/doctor-dashboard" sx={{ textTransform: 'none' }} 
                        />
                        <Tab label={t('register_child_tab')} sx={{ textTransform: 'none' }} />
                        {userRole === 'admin' && (
                            <Tab label="Manage Therapists" component={Link} to="/doctor-dashboard" onClick={() => localStorage.setItem('dashboardTab', '2')} sx={{ textTransform: 'none' }} />
                        )}
                    </Tabs>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, flexGrow: 1 }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>{t('register_child_main_title')}</Typography>
                    <Typography variant="body1" color="text.secondary">{t('register_child_main_subtitle')}</Typography>
                </Box>

                {/* Progress Stepper */}
                <Box sx={{ width: '100%', mb: 4 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepLabel StepIconComponent={(props) => (
                                    <Box sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        bgcolor: props.completed ? '#4caf50' : (props.active ? '#00bcd4' : '#bdbdbd'),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.875rem'
                                    }}>
                                        {props.completed ? <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> : (index + 1)}
                                    </Box>
                                )}>
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <LinearProgress variant="determinate" value={progress} sx={{ flexGrow: 1, mr: 1, height: 8, borderRadius: 5 }} />
                        <Typography variant="body2" color="text.secondary">{Math.round(progress)}% {t('complete_text')}</Typography>
                    </Box>
                </Box>

                <form onSubmit={handleSubmit}>
                    {getStepContent(activeStep)}
                </form>

                {/* Navigation Footer */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: 'center', mt: 4, py: 2 }}>
                    <Button
                        onClick={handleBack}
                        disabled={activeStep === 0}
                        sx={{ textTransform: 'none' }}
                    >
                        {t('previous_link')}
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                        {t('step_info_message', { current: activeStep + 1, total: steps.length })}
                    </Typography>
                    <Box sx={{ width: 100 }} /> {/* Placeholder to balance space */}
                </Box>
            </Container>
        </Box>
    );
};

export default ChildRegistration;
